/**
 * Sidebar v2 thread model: the client-side overlay that gives forge threads
 * the pin/order/snooze fields upstream persists on its thread records, and
 * the section partition (pinned / active / snoozed / settled) built on the
 * shared sort implementations.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (partition memo, ~lines 2525-2632) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: forge persists settle server-side and
 * pin/order/snooze in the uiState overlay (see uiStateStore), so
 * classification reads the overlay instead of thread fields, and the
 * per-environment capability gates do not exist in forge (every listed
 * action is available). Snooze classification uses the shared
 * effectiveSnoozed rules from @t3tools/client-runtime.
 */
import { scopeThreadRef, scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";

import type { SidebarThreadSummary } from "~/types";

import { applySidebarThreadDrop } from "./sidebarV2DropPlan";
import type { SidebarSection } from "./sidebarV2List";
import { orderItemsByPreferredIds } from "./sidebarV2List";
import { effectiveSnoozed } from "./sidebarV2Settled";
import { sortSettledThreadsForSidebar } from "./sidebarV2Search";
import {
  sortActiveThreadsByOrderKey as sortThreadsForSidebar,
  sortPinnedThreadsByOrderKey as sortPinnedThreadsForSidebar,
} from "./sidebarV2Sort";

/** The lifecycle fields the v2 sidebar overlays onto forge thread summaries.
 * Forge carries `settledOverride`/`settledAt` on the thread itself; the rest
 * come from the uiState store. `unsettledAt` has no forge writer yet (it is
 * an upstream server stamp), so the overlay types it but no store writes it
 * and the active sort anchor stays on `createdAt`. */
export type SidebarV2Thread = SidebarThreadSummary & {
  readonly pinnedAt: string | null;
  readonly pinOrderKey: string | null;
  readonly activeOrderKey: string | null;
  readonly snoozedAt: string | null;
  readonly snoozedUntil: string | null;
  readonly unsettledAt: string | null;
  readonly backgroundLiveness: "working" | "monitoring" | null;
};

/** The canonical scoped thread key (`scopedThreadKey`) that every v2 list,
 * drag, overlay, and route path keys on: pin, snooze, and route writers use
 * the encoded form, so raw `${environmentId}:${id}` reads would miss any
 * thread whose id contains a colon. */
export function sidebarThreadKey(thread: {
  readonly environmentId: EnvironmentId;
  readonly id: ThreadId;
}): string {
  return scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id));
}

export interface SidebarOverlayState {
  readonly pinnedThreadById: Readonly<Record<string, boolean>>;
  readonly pinOrderKeyById: Readonly<Record<string, string>>;
  readonly activeOrderKeyById: Readonly<Record<string, string>>;
  readonly threadSnoozeById: Readonly<
    Record<string, { readonly snoozedAt: string; readonly snoozedUntil: string }>
  >;
}

/** Merge one forge summary with the overlay state for its scoped key.
 * `pinnedAt` needs only to be non-null for classification; the summary's
 * own timestamps are the stable stand-in because nothing reads the value. */
export function applySidebarOverlay(
  thread: SidebarThreadSummary,
  threadKey: string,
  overlay: SidebarOverlayState,
): SidebarV2Thread {
  const pinned = overlay.pinnedThreadById[threadKey] === true;
  const snooze = overlay.threadSnoozeById[threadKey] ?? null;
  const stamp = thread.updatedAt ?? thread.createdAt;
  return {
    ...thread,
    pinnedAt: pinned ? stamp : null,
    pinOrderKey: overlay.pinOrderKeyById[threadKey] ?? null,
    activeOrderKey: overlay.activeOrderKeyById[threadKey] ?? null,
    snoozedAt: snooze?.snoozedAt ?? null,
    snoozedUntil: snooze?.snoozedUntil ?? null,
    unsettledAt: null,
    backgroundLiveness: null,
  };
}

/** A drop held open while its order-key writes land; the partition projects
 * the moved thread so the preview stays put until canonical state agrees. */
export interface SidebarOptimisticDrop {
  readonly key: string;
  readonly sourceSection: SidebarSection;
  readonly section: "pinned" | "active" | "settled";
  readonly occurredAt: string;
  readonly clearsSnooze: boolean;
  /** Full destination order for pinned and active drops. */
  readonly order: readonly string[] | null;
  /** The keys this drop writes; the hold releases once all appear. */
  readonly assignedKeys: ReadonlyMap<string, string>;
}

export interface SidebarPartition {
  readonly pinnedThreads: readonly SidebarV2Thread[];
  readonly activeThreads: readonly SidebarV2Thread[];
  readonly snoozedThreads: readonly SidebarV2Thread[];
  readonly settledThreads: readonly SidebarV2Thread[];
  readonly snoozeNow: string;
}

/** Push one classified thread into its section bucket: snooze outranks
 * settlement and pinning until the thread wakes, then settled, then pinned,
 * then the active inbox. */
function pushClassified(
  buckets: {
    readonly pinned: SidebarV2Thread[];
    readonly active: SidebarV2Thread[];
    readonly snoozed: SidebarV2Thread[];
    readonly settled: SidebarV2Thread[];
  },
  thread: SidebarV2Thread,
  snoozedNow: boolean,
): void {
  if (snoozedNow) {
    buckets.snoozed.push(thread);
  } else if (thread.settledOverride === "settled") {
    buckets.settled.push(thread);
  } else if (thread.pinnedAt != null) {
    buckets.pinned.push(thread);
  } else {
    buckets.active.push(thread);
  }
}

/** Filter visible projects and partition threads into ordered shelves using current snooze times and any optimistic drop. */
// fallow-ignore-next-line complexity -- decomposed from the byte-faithful upstream projection and reorder planner (pin e3e7cc3f, #3601 precedent)
export function partitionSidebarV2Threads(input: {
  readonly threads: readonly SidebarV2Thread[];
  /** Scoped keys of the scoped-to project group, or null for all projects. */
  readonly scopedProjectKeys: ReadonlySet<string> | null;
  readonly optimisticDrop: SidebarOptimisticDrop | null;
  /** Bumped exactly at the next snooze wake boundary so woken threads leave
   * the shelf on time; a plain minute tick would hold them up to a minute. */
  readonly snoozeWakeTick: number;
}): SidebarPartition {
  const { threads, scopedProjectKeys, optimisticDrop } = input;
  // Snooze classification uses a REAL clock, not the quantized minute: wake
  // times are second-precise and a woken thread must not linger on the shelf
  // for the rest of the minute. snoozeWakeTick re-runs this exactly at the
  // next wake boundary.
  void input.snoozeWakeTick;
  const preciseNow = new Date().toISOString();
  void input.snoozeWakeTick;
  const visible = threads.filter(
    (thread) =>
      thread.archivedAt === null &&
      (scopedProjectKeys === null ||
        scopedProjectKeys.has(`${thread.environmentId}:${thread.projectId}`)),
  );
  const pinned: SidebarV2Thread[] = [];
  const active: SidebarV2Thread[] = [];
  const snoozed: SidebarV2Thread[] = [];
  const settled: SidebarV2Thread[] = [];
  for (const thread of visible) {
    const threadKey = sidebarThreadKey(thread);
    if (optimisticDrop?.key === threadKey) {
      const projected = applySidebarThreadDrop(
        thread,
        optimisticDrop.section,
        optimisticDrop.occurredAt,
        optimisticDrop.assignedKeys.get(threadKey),
      );
      const keepsSnooze = !optimisticDrop.clearsSnooze;
      (optimisticDrop.section === "pinned"
        ? pinned
        : optimisticDrop.section === "settled"
          ? settled
          : active
      ).push(
        keepsSnooze
          ? {
              ...thread,
              ...projected,
              snoozedAt: thread.snoozedAt,
              snoozedUntil: thread.snoozedUntil,
            }
          : { ...thread, ...projected },
      );
      continue;
    }
    pushClassified(
      { pinned, active, snoozed, settled },
      thread,
      effectiveSnoozed(thread, { now: preciseNow }),
    );
  }
  // One shared rule on every platform (see sortPinnedThreadsByOrderKey):
  // user-arranged keys first, keyless threads in creation order below.
  const sortedPinned = sortPinnedThreadsForSidebar(pinned);
  const sortedActive = sortThreadsForSidebar(active);
  return {
    pinnedThreads:
      optimisticDrop?.section !== "pinned" || optimisticDrop.order === null
        ? sortedPinned
        : orderItemsByPreferredIds({
            items: sortedPinned,
            preferredIds: optimisticDrop.order,
            getId: (thread) => sidebarThreadKey(thread),
          }),
    activeThreads:
      optimisticDrop?.section !== "active" || optimisticDrop.order === null
        ? sortedActive
        : orderItemsByPreferredIds({
            items: sortedActive,
            preferredIds: optimisticDrop.order,
            getId: (thread) => sidebarThreadKey(thread),
          }),
    // Soonest wake first: "what comes back next" is the shelf's question.
    snoozedThreads: snoozed.toSorted(
      (left, right) =>
        (left.snoozedUntil == null ? 0 : Date.parse(left.snoozedUntil)) -
        (right.snoozedUntil == null ? 0 : Date.parse(right.snoozedUntil)),
    ),
    settledThreads: sortSettledThreadsForSidebar(settled),
    snoozeNow: preciseNow,
  };
}

/** Project scope keys for one project group: the representative project
 * itself plus its member refs, so selecting a group matches threads
 * anchored to any of its projects, including when a group carries no
 * member refs at all. */
export function sidebarProjectScopeMemberKeys(project: {
  readonly environmentId: string;
  readonly id: string;
  readonly memberProjectRefs?:
    | readonly { readonly environmentId: string; readonly projectId: string }[]
    | null;
}): readonly string[] {
  return [
    `${project.environmentId}:${project.id}`,
    ...(project.memberProjectRefs ?? []).map((ref) => `${ref.environmentId}:${ref.projectId}`),
  ];
}
